/**
 * WHAT NEEDS DOING, IN THE ORDER IT NEEDS DOING.
 *
 * The first screen after signing in used to be the job list: forty
 * photographs of houses, newest first, which is a gallery rather than a
 * morning. A gallery does not tell a contractor that a bid went out on a
 * length nobody walked, or that a job was approved in September and the garage
 * has been redrawn since.
 *
 * UNKNOWN IS NOT ZERO, AND THAT IS THE RULE THIS FILE IS BUILT ON. TOD-01
 * lists follow-ups, unsigned proposals, crew status, weather and material
 * shortages. Leads, scheduling, crews and stock do not exist in this product
 * yet, and a dashboard that showed "0 crews late" would be lying in the most
 * expensive way a dashboard can - by looking answered. So the things that
 * cannot be known are reported as not available, with the reason, and the
 * things that can be known are computed from real saved jobs.
 *
 * Nothing here reads a network, a clock service or a model. Every number comes
 * off documents the person already has.
 */

export type TodoKind =
  | 'drifted'
  | 'unmeasured'
  | 'unpaid'
  | 'nameless'

export interface JobFacts {
  id: string
  name: string
  customer: string
  address: string
  savedAt: string
  /** Feet on included sections that nobody has measured at the house. */
  unmeasuredFeet: number
  /** Included sections carrying no length at all. */
  missingLengths: number
  /** How many things have moved since this job's scope was approved. */
  driftCount: number
  /** Money still owed, in whatever the quote is in. Zero when nothing is due. */
  balance: number
  /** True when there is a quote or invoice at all. */
  hasPaper: boolean
  /**
   * WHETHER THE PAPER IS AN INVOICE.
   *
   * A quote nobody has answered is not money owed. Both carry a balance,
   * because both are the same document with its kind changed, and reading the
   * balance without reading the kind put four unanswered quotes into the same
   * sentence as one real invoice: five things to chase, four of which nobody
   * has agreed to pay. Whether somebody might pay is the question Estimates
   * answers. This one is who owes.
   */
  invoiced: boolean
}

export interface Todo {
  kind: TodoKind
  jobId: string
  /** What it is, in the words a person would use. */
  said: string
  /** The job, so a row can say which house without a second lookup. */
  where: string
}

/** Something the product genuinely cannot answer yet, and why. */
export interface NotKnown {
  label: string
  because: string
}

export interface Today {
  todos: Todo[]
  notKnown: NotKnown[]
  /** Jobs looked at. Zero means no saved jobs, which is a real answer. */
  jobs: number
}

const houseOf = (j: JobFacts): string =>
  j.customer.trim() || j.address.trim() || j.name.trim() || 'Untitled job'

/**
 * THE ORDER IS THE POINT.
 *
 * A scope that has moved since somebody agreed to it is first, because it is
 * the one that costs money in an argument. An unwalked length is next, because
 * it is the one that costs money on the truck. Money owed is third: it is the
 * most visible and the least urgent, since nothing gets worse by knowing about
 * it an hour later. A job nobody can find in December is last and is still
 * worth saying.
 */
const RANK: Record<TodoKind, number> = {
  drifted: 0,
  unmeasured: 1,
  unpaid: 2,
  nameless: 3,
}

export function today(jobs: JobFacts[], money: (n: number) => string): Today {
  const todos: Todo[] = []

  for (const j of jobs) {
    const where = houseOf(j)

    if (j.driftCount > 0) {
      todos.push({
        kind: 'drifted',
        jobId: j.id,
        where,
        said:
          j.driftCount === 1
            ? 'One thing has changed since this scope was approved.'
            : `${j.driftCount} things have changed since this scope was approved.`,
      })
    }

    if (j.missingLengths > 0) {
      todos.push({
        kind: 'unmeasured',
        jobId: j.id,
        where,
        said:
          j.missingLengths === 1
            ? 'A section has no length on it.'
            : `${j.missingLengths} sections have no length on them.`,
      })
    } else if (j.unmeasuredFeet > 0) {
      todos.push({
        kind: 'unmeasured',
        jobId: j.id,
        where,
        said: `${Math.round(j.unmeasuredFeet)} ft has not been measured at the house.`,
      })
    }

    if (j.hasPaper && j.invoiced && j.balance > 0) {
      todos.push({
        kind: 'unpaid',
        jobId: j.id,
        where,
        said: `${money(j.balance)} still owed.`,
      })
    }

    if (!j.customer.trim() && !j.address.trim()) {
      todos.push({
        kind: 'nameless',
        jobId: j.id,
        where,
        said: 'No customer and no address, so this cannot be found by name.',
      })
    }
  }

  todos.sort((a, b) => {
    const r = RANK[a.kind] - RANK[b.kind]
    if (r !== 0) return r
    return a.where.localeCompare(b.where)
  })

  return { todos, notKnown: NOT_KNOWN, jobs: jobs.length }
}

/**
 * The parts of TOD-01 this product cannot answer, said out loud.
 *
 * Every line here is a feature that does not exist rather than a number that
 * happens to be zero, and each one names what would have to be built. When one
 * of them ships it comes off this list and becomes a real count.
 */
export const NOT_KNOWN: NotKnown[] = [
  /*
   * Today's visits came off this list when the schedule shipped: they are on
   * this screen now, above the work, and a stop can be started and finished.
   * Crew status stays, because there is still one login and nobody to be.
   */
  /*
   * Crew status came off this list when the roster shipped: a visit can name
   * who is going and Today shows it on the stop. What is still missing is
   * anything a person reports themselves, because none of them can sign in.
   */
  { label: 'Where a crew actually is', because: 'Nobody on the roster can sign in to say.' },
  { label: 'Leads needing follow-up', because: 'The lead pipeline is not built yet.' },
  { label: 'Proposals awaiting signature', because: 'Nothing records a customer signing yet.' },
  { label: 'Material shortages', because: 'There is no stock count behind the materials list.' },
  { label: 'Weather disruptions', because: 'No weather service is connected.' },
]
